import { useQueryClient } from '@tanstack/react-query';

import { useFishGroupsController_close } from '@/services/hooks';
import type { CloseFishGroupRequest } from '@/services/types';

import type { MutationState } from '../shared/mutation';

import { invalidateFishGroupQueries } from './_invalidateQueries';

export interface UseCloseFishGroupResult extends MutationState {
    close: (requestBody: CloseFishGroupRequest) => Promise<void>;
}

interface UseCloseFishGroupOptions {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

export const useCloseFishGroup = (
    clientId: number,
    fishGroupId: number,
    options?: UseCloseFishGroupOptions
): UseCloseFishGroupResult => {
    const queryClient = useQueryClient();
    const mutation = useFishGroupsController_close();

    const close = async (requestBody: CloseFishGroupRequest): Promise<void> => {
        try {
            await mutation.mutateAsync({
                clientId,
                fishGroupId,
                requestBody,
            });
            await invalidateFishGroupQueries(queryClient, clientId, fishGroupId);
            options?.onSuccess?.();
        } catch (error) {
            options?.onError?.(error);
            throw error;
        }
    };

    return {
        close,
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
